import { useMemo } from "react";
import type { CSSProperties } from "react";
import {
  BLOCK_DEFINITIONS,
  TOOL_DEFINITIONS,
  useEditorStore
} from "../store/editorStore";
import { BlockStateEditor } from "./BlockStateEditor";

export function BuildPanel() {
  const blockCatalogRevision = useEditorStore(
    (state) => state.blockCatalogRevision
  );
  const blockSearch = useEditorStore((state) => state.blockSearch);
  const setBlockSearch = useEditorStore(
    (state) => state.setBlockSearch
  );
  const blockCategory = useEditorStore(
    (state) => state.blockCategory
  );
  const setBlockCategory = useEditorStore(
    (state) => state.setBlockCategory
  );
  const projectVersionId = useEditorStore(
    (state) => state.projectVersionId
  );
  const activeTool = useEditorStore((state) => state.activeTool);
  const setActiveTool = useEditorStore(
    (state) => state.setActiveTool
  );
  const activeBlockId = useEditorStore(
    (state) => state.activeBlockId
  );
  const setActiveBlock = useEditorStore(
    (state) => state.setActiveBlock
  );
  const brushSize = useEditorStore((state) => state.brushSize);
  const setBrushSize = useEditorStore(
    (state) => state.setBrushSize
  );
  const mirrorX = useEditorStore((state) => state.mirrorX);
  const setMirrorX = useEditorStore((state) => state.setMirrorX);
  const generateGatehouse = useEditorStore(
    (state) => state.generateGatehouse
  );
  const generateModelLab = useEditorStore(
    (state) => state.generateModelLab
  );
  const generateSpecialRendererLab = useEditorStore(
    (state) => state.generateSpecialRendererLab
  );
  const clearWorld = useEditorStore((state) => state.clearWorld);

  void blockCatalogRevision;

  const categories = useMemo(() => {
    return [
      "All",
      ...Array.from(
        new Set(
          BLOCK_DEFINITIONS.map(
            (definition) => definition.category ?? "Building"
          )
        )
      ).sort()
    ];
  }, [blockCatalogRevision]);

  const filteredDefinitions = useMemo(() => {
    const query = blockSearch.trim().toLowerCase();

    return BLOCK_DEFINITIONS
      .filter((definition) => {
        if (
          blockCategory !== "All" &&
          (definition.category ?? "Building") !== blockCategory
        ) {
          return false;
        }

        if (!query) return true;

        return (
          definition.id.includes(query) ||
          definition.name.toLowerCase().includes(query)
        );
      })
      .sort((first, second) =>
        first.name.localeCompare(second.name)
      );
  }, [blockCatalogRevision, blockCategory, blockSearch]);

  const activeDefinition = BLOCK_DEFINITIONS.find(
    (definition) => definition.id === activeBlockId
  );

  return (
    <aside className="tool-panel left-panel">
      <section className="panel-section">
        <div className="panel-heading">
          <span>Build tools</span>
          <span className="keyboard-hint">1–4</span>
        </div>
        <div className="tool-grid">
          {TOOL_DEFINITIONS.map((tool) => (
            <button
              key={tool.id}
              type="button"
              className={
                "tool-button" +
                (tool.id === activeTool ? " active" : "")
              }
              onClick={() => setActiveTool(tool.id)}
            >
              <span className="tool-icon">{tool.icon}</span>
              <span className="tool-name">{tool.name}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="panel-section">
        <div className="panel-heading">
          <span>Block palette</span>
          <span className="panel-badge">
            {BLOCK_DEFINITIONS.length.toLocaleString()} · Java {projectVersionId}
          </span>
        </div>

        <div className="block-catalog-controls">
          <input
            className="block-search-input"
            type="search"
            value={blockSearch}
            placeholder="Search blocks…"
            onChange={(event) => setBlockSearch(event.target.value)}
          />
          <select
            className="block-category-select"
            value={blockCategory}
            onChange={(event) => setBlockCategory(event.target.value)}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="block-catalog-summary">
          <strong>{filteredDefinitions.length.toLocaleString()}</strong>
          <span>matching blocks</span>
          <span>·</span>
          <span>{activeDefinition?.name ?? activeBlockId}</span>
        </div>

        <div className="block-palette expanded-palette">
          {filteredDefinitions.map((definition) => (
            <button
              key={definition.id}
              type="button"
              className={
                "block-swatch" +
                (definition.id === activeBlockId ? " active" : "")
              }
              data-short={definition.shortName}
              data-stateful={String(
                Object.keys(definition.stateOptions ?? {}).length > 0 ||
                  Boolean(definition.connectionType) ||
                  Boolean(definition.placementType)
              )}
              style={
                {
                  "--swatch-color": definition.color
                } as CSSProperties
              }
              title={
                definition.name +
                " · minecraft:" +
                definition.id +
                (definition.availableInVersion
                  ? " · Java " + definition.availableInVersion
                  : "")
              }
              onClick={() => {
                setActiveBlock(definition.id);
                setActiveTool("place");
              }}
            />
          ))}
        </div>
        <BlockStateEditor />
      </section>

      <section className="panel-section compact-section">
        <label className="field-label">
          Brush radius
          <span className="field-value">{brushSize}</span>
        </label>
        <input
          className="range-control"
          type="range"
          min="1"
          max="4"
          value={brushSize}
          onChange={(event) =>
            setBrushSize(Number(event.target.value))
          }
        />
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={mirrorX}
            onChange={(event) => setMirrorX(event.target.checked)}
          />
          <span className="toggle-visual" />
          <span>Mirror across X = 0</span>
        </label>
      </section>

      <section className="panel-section">
        <div className="panel-heading">
          <span>Architecture</span>
          <span className="panel-badge panel-badge-gold">
            Vanilla Models
          </span>
        </div>
        <p className="panel-copy">
          AI-generated architecture remains editable as Minecraft
          blocks and block states.
        </p>
        <button
          className="wide-action-button"
          type="button"
          onClick={generateGatehouse}
        >
          Generate gatehouse
        </button>
        <button
          className="wide-action-button secondary-action"
          type="button"
          onClick={generateModelLab}
        >
          Generate vanilla model lab
        </button>
        <button
          className="wide-action-button secondary-action"
          type="button"
          onClick={generateSpecialRendererLab}
        >
          Generate special renderer lab
        </button>
        <button
          className="wide-action-button secondary-action"
          type="button"
          onClick={clearWorld}
        >
          Clear world
        </button>
      </section>
    </aside>
  );
}
